<template>
	<div>
		<v-row align="center" class="mb-2">
			<v-col cols="12" md="6">
				<h1 class="headline mb-0">Financieel overzicht</h1>
				<div class="caption text--secondary">
					Rekeningoverzicht per opleiding en schooljaar
				</div>
			</v-col>
			<v-col cols="12" md="6" class="text-md-right" v-if="!loading && statements.length">
				<div class="body-2">
					Totaal debet: <strong>{{ formatAmount(grandTotals.debit) }} ANG</strong>
				</div>
				<div class="body-2">
					Totaal credit: <strong>{{ formatAmount(grandTotals.credit) }} ANG</strong>
				</div>
				<div class="title mt-1" :class="grandTotals.balance > 0 ? 'error--text' : 'success--text'">
					Saldo: {{ formatAmount(grandTotals.balance) }} ANG
				</div>
			</v-col>
		</v-row>

		<v-alert
			type="info"
			border="left"
			colored-border
			dense
			class="mb-4"
		>
			<strong>Disclaimer:</strong>
			Aan de in dit financieel overzicht getoonde gegevens kunnen geen rechten worden ontleend.
			Dit overzicht is uitsluitend bedoeld ter informatie en vormt geen grondslag voor juridische
			stappen, aanspraken of verplichtingen. Bij vragen of onduidelijkheden kunt u contact
			opnemen met de schooladministratie.
		</v-alert>

		<v-alert
			v-if="errorMessage"
			type="error"
			border="right"
			colored-border
			elevation="2"
			class="mb-4"
		>{{ errorMessage }}</v-alert>

		<div v-if="loading" class="text-center py-8">
			<v-progress-circular indeterminate color="primary"></v-progress-circular>
		</div>

		<template v-else-if="!statements.length">
			<v-card class="elevation-2 pa-6">
				Geen financiële transacties gevonden.
			</v-card>
		</template>

		<template v-else>
			<v-card
				v-for="group in statements"
				:key="group.key"
				class="elevation-2 mb-6 statement-card"
			>
				<v-toolbar flat dense color="primary" dark>
					<v-toolbar-title class="body-1">
						{{ group.studyName }} — {{ group.schoolyearName }}
					</v-toolbar-title>
				</v-toolbar>

				<div class="statement-table-wrap">
					<table class="statement-table">
						<thead>
							<tr>
								<th class="col-date">Datum</th>
								<th class="col-desc">Omschrijving</th>
								<th class="col-task">Kostenplaat</th>
								<th class="col-amount text-right">Debet</th>
								<th class="col-amount text-right">Credit</th>
								<th class="col-amount text-right">Saldo</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in group.rows" :key="row.id">
								<td class="col-date">{{ formatDate(row.registration_date) }}</td>
								<td class="col-desc">
									{{ row.description || row.transactionTypeName || "—" }}
								</td>
								<td class="col-task">{{ row.financeTaskName || "—" }}</td>
								<td class="col-amount text-right">
									{{ row.debit != null ? formatAmount(row.debit) : "" }}
								</td>
								<td class="col-amount text-right">
									{{ row.credit != null ? formatAmount(row.credit) : "" }}
								</td>
								<td class="col-amount text-right">{{ formatAmount(row.runningBalance) }}</td>
							</tr>
						</tbody>
						<tfoot>
							<tr class="totals-row">
								<td colspan="3" class="font-weight-medium">Totaal {{ group.studyName }}</td>
								<td class="col-amount text-right font-weight-medium">
									{{ formatAmount(group.totalDebit) }}
								</td>
								<td class="col-amount text-right font-weight-medium">
									{{ formatAmount(group.totalCredit) }}
								</td>
								<td
									class="col-amount text-right font-weight-bold"
									:class="group.balance > 0 ? 'error--text' : 'success--text'"
								>
									{{ formatAmount(group.balance) }}
								</td>
							</tr>
						</tfoot>
					</table>
				</div>
			</v-card>
		</template>
	</div>
</template>

<script>
function isCreditType(row) {
	var type = row && row.tbl_sys_transaction_type;
	if (!type) return false;
	if (Number(type.id) === 2) return true;
	var name = String(type.name || "")
		.trim()
		.toLowerCase();
	return name === "credit" || name.indexOf("credit") === 0 || name === "betaling";
}

function isDebitType(row) {
	var type = row && row.tbl_sys_transaction_type;
	if (!type) return !isCreditType(row);
	if (Number(type.id) === 1) return true;
	var name = String(type.name || "")
		.trim()
		.toLowerCase();
	return (
		name === "debit" ||
		name === "debet" ||
		name.indexOf("debit") === 0 ||
		name.indexOf("debet") === 0
	);
}

/** Stable YYYYMMDD number for sorting date-only / ISO values. */
function dateSortKey(value) {
	if (!value) return 0;
	if (value instanceof Date && !isNaN(value.getTime())) {
		return (
			value.getFullYear() * 10000 +
			(value.getMonth() + 1) * 100 +
			value.getDate()
		);
	}
	var raw = String(value).trim();
	var m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (m) {
		return Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
	}
	var d = new Date(raw);
	if (isNaN(d.getTime())) return 0;
	return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

export default {
	name: "financieel",
	data: function() {
		return {
			loading: false,
			errorMessage: null,
			transactions: []
		};
	},
	computed: {
		statements() {
			var groups = {};
			var order = [];

			(this.transactions || []).forEach(function(tx) {
				var study = tx.tbl_sys_study || {};
				var year = tx.tbl_sys_schoolyear || {};
				var studyId = study.id != null ? study.id : "x";
				var yearId = year.id != null ? year.id : "x";
				var key = String(studyId) + "|" + String(yearId);

				if (!groups[key]) {
					groups[key] = {
						key: key,
						studyId: studyId,
						schoolyearId: yearId,
						studyName: study.name || "Onbekende opleiding",
						schoolyearName: year.name || "Onbekend schooljaar",
						rows: [],
						totalDebit: 0,
						totalCredit: 0
					};
					order.push(key);
				}

				var amount = Number(tx.amount);
				if (isNaN(amount)) amount = 0;
				var credit = isCreditType(tx);
				var debit = !credit && isDebitType(tx);
				// If type is unknown, treat as debit (charge)
				if (!credit && !debit) {
					debit = true;
				}

				groups[key].rows.push({
					id: tx.id,
					registration_date: tx.registration_date,
					description: tx.description,
					transactionTypeName:
						tx.tbl_sys_transaction_type && tx.tbl_sys_transaction_type.name,
					financeTaskName:
						tx.tbl_sys_finance_task && tx.tbl_sys_finance_task.name,
					debit: debit ? amount : null,
					credit: credit ? amount : null,
					sortKey: dateSortKey(tx.registration_date)
				});

				if (debit) groups[key].totalDebit += amount;
				if (credit) groups[key].totalCredit += amount;
			});

			return order
				.map(function(key) {
					var g = groups[key];
					g.rows.sort(function(a, b) {
						if (a.sortKey !== b.sortKey) {
							return a.sortKey - b.sortKey;
						}
						return Number(a.id || 0) - Number(b.id || 0);
					});

					var running = 0;
					g.rows.forEach(function(row) {
						running += Number(row.debit || 0) - Number(row.credit || 0);
						row.runningBalance = running;
					});

					g.balance = g.totalDebit - g.totalCredit;
					g.firstDateKey = g.rows.length ? g.rows[0].sortKey : 0;
					return g;
				})
				.sort(function(a, b) {
					var y = String(b.schoolyearName).localeCompare(
						String(a.schoolyearName),
						"nl"
					);
					if (y !== 0) return y;
					return String(a.studyName).localeCompare(String(b.studyName), "nl");
				});
		},
		grandTotals() {
			var debit = 0;
			var credit = 0;
			this.statements.forEach(function(g) {
				debit += g.totalDebit;
				credit += g.totalCredit;
			});
			return {
				debit: debit,
				credit: credit,
				balance: debit - credit
			};
		}
	},
	created() {
		this.loadFinance();
	},
	methods: {
		formatDate(value) {
			if (!value) return "—";
			var d = new Date(value);
			if (isNaN(d.getTime())) return String(value).slice(0, 10);
			var day = String(d.getDate()).padStart(2, "0");
			var month = String(d.getMonth() + 1).padStart(2, "0");
			var year = d.getFullYear();
			return day + "-" + month + "-" + year;
		},
		formatAmount(value) {
			var n = Number(value);
			if (isNaN(n)) return "—";
			return n.toLocaleString("nl-NL", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			});
		},
		async loadFinance() {
			this.loading = true;
			this.errorMessage = null;
			try {
				var result = await this.$store.dispatch("fetchStudentFinance");
				this.transactions = (result && result.transactions) || [];
			} catch (err) {
				console.error("Student finance load failed:", err);
				this.errorMessage =
					(err && err.message) ||
					"Financiële gegevens konden niet worden geladen.";
				this.transactions = [];
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>

<style scoped>
.statement-card {
	overflow: hidden;
}
.statement-table-wrap {
	overflow-x: auto;
}
.statement-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.9rem;
}
.statement-table th,
.statement-table td {
	padding: 10px 14px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	vertical-align: top;
}
.statement-table thead th {
	background: #f5f5f5;
	font-weight: 600;
	white-space: nowrap;
}
.statement-table tbody tr:nth-child(even) {
	background: #fafafa;
}
.statement-table .totals-row td {
	border-bottom: none;
	border-top: 2px solid rgba(0, 0, 0, 0.2);
	background: #f0f0f0;
}
.col-date {
	width: 110px;
	white-space: nowrap;
}
.col-amount {
	width: 110px;
	white-space: nowrap;
	font-variant-numeric: tabular-nums;
}
.col-task {
	width: 160px;
}
.text-right {
	text-align: right;
}
</style>
